import React, { useState, useEffect } from 'react';

interface UsePolicyApplicationStepSelectionProps {
  latestStep?: PolicyApplicationStep | null;
  allStepsCompleted: boolean;
}

interface UsePolicyApplicationStepSelectionReturn {
  selectedStepId: string | null;
  setSelectedStepId: React.Dispatch<React.SetStateAction<string | null>>;
}

/**
 * Custom hook to manage step selection in the policy application stepper.
 *
 * Features:
 * - Auto-selects the current step on initial load
 * - Automatically follows progress when latestStep advances
 * - Honors manual step selections by users
 * - Prevents interference between automatic and manual selection
 */
export function usePolicyApplicationStepSelection({
  latestStep,
  allStepsCompleted,
}: UsePolicyApplicationStepSelectionProps): UsePolicyApplicationStepSelectionReturn {
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [lastKnownLatestStepType, setLastKnownLatestStepType] = useState<string | null>(null);

  // Auto-select current step on component mount (only once)
  useEffect(() => {
    if (latestStep && !hasAutoSelected && !allStepsCompleted) {
      setSelectedStepId(latestStep.stepType);
      setLastKnownLatestStepType(latestStep.stepType);
      setHasAutoSelected(true);
    }
  }, [latestStep, hasAutoSelected, allStepsCompleted]);

  // Auto-follow progress when latestStep advances (but honor manual selections afterward)
  useEffect(() => {
    if (
      latestStep &&
      lastKnownLatestStepType &&
      latestStep.stepType !== lastKnownLatestStepType &&
      !allStepsCompleted
    ) {
      // Progress has advanced to a new step - automatically select it
      setSelectedStepId(latestStep.stepType);
      setLastKnownLatestStepType(latestStep.stepType);
    } else if (latestStep && latestStep.stepType !== lastKnownLatestStepType) {
      // Update our tracking even if we don't auto-select
      setLastKnownLatestStepType(latestStep.stepType);
    }
  }, [latestStep, lastKnownLatestStepType, allStepsCompleted]);

  return {
    selectedStepId,
    setSelectedStepId,
  };
}
